import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconSwap } from '@/components/icon-swap';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SidebarLeft01Icon, SidebarRight01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useUserInfoQuery } from '../services/api';
import UserInfo from './sidebar/UserInfo';
import SidebarMenu from './sidebar/SidebarMenu';
import { SidebarMenuProvider } from './sidebar/SidebarMenuContext';
import CommandPaletteTrigger from '../utils/components/commandPalette/CommandPaletteTrigger';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSidebarLogoConfig } from '../redux/slices/branding/brandingSlice';

const SidebarUserSkeleton = ({ collapsed }) => (
  <div
    className={cn(
      'w-full cursor-default rounded-lg p-2 transition-colors duration-150 ease-out-quad hover:bg-foreground/5 max-sm:px-1',
      collapsed && 'px-1'
    )}
  >
    <div className={cn('flex w-full items-center justify-between', collapsed && 'justify-center')}>
      <div className={cn('flex min-w-0 items-center gap-3', collapsed ? 'flex-none' : 'flex-1')}>
        <Skeleton className={cn('shrink-0 rounded-full', collapsed ? 'size-8' : 'size-9')} />
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-full rounded-sm" />
            <Skeleton className="h-3 w-[72%] rounded-sm" />
          </div>
        )}
      </div>
      {!collapsed && <Skeleton className="size-6 shrink-0 rounded-full" />}
    </div>
  </div>
);

function GlobalSidebar({ collapsed = false, onToggleCollapse, showCollapseToggle = false }) {
  const { data, isLoading } = useUserInfoQuery();
  const branding = useSelector(state => state.branding.branding);
  const isDarkTheme = useSelector(state => state.app.isDarkTheme);
  const hasUserInfo = Boolean(data?.data?.firstname || data?.data?.lastname || data?.data?.email);
  const showUserSkeleton = isLoading || !hasUserInfo;

  const { src: logoSrc, isClientLogo } = getSidebarLogoConfig({
    brandingEnabled: branding.brandingEnabled,
    logoUrl: branding.logoUrl,
    isDarkTheme,
  });

  return (
    <SidebarMenuProvider>
      <div className="flex h-full flex-col overflow-hidden">
        <div
          className={cn(
            'shrink-0 px-3 pt-3 pb-2 sm:pt-3.5 sm:pb-2.5',
            collapsed && 'px-2 sm:pt-3 sm:pb-2'
          )}
        >
          <div
            className={cn(
              'flex min-h-6 items-center justify-between gap-2',
              collapsed && 'justify-center'
            )}
          >
            {!collapsed && (
              <Link
                to="/"
                className="flex min-w-0 flex-1 animate-in items-center leading-none duration-300 ease-out-quad fade-in-0"
              >
                <img
                  alt="Company"
                  className={cn(
                    'block max-h-5 w-auto max-w-[76px] object-contain sm:max-h-[22px] sm:max-w-[84px]',
                    isClientLogo && 'dark:opacity-[0.92] dark:brightness-0 dark:invert'
                  )}
                  src={logoSrc}
                  key={logoSrc}
                />
              </Link>
            )}
            {showCollapseToggle && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={cn('size-7 text-muted-foreground', collapsed && 'mx-auto')}
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  >
                    <IconSwap swapKey={collapsed ? 'expand' : 'collapse'}>
                      <HugeiconsIcon
                        icon={collapsed ? SidebarRight01Icon : SidebarLeft01Icon}
                        strokeWidth={2}
                      />
                    </IconSwap>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <div className={cn('shrink-0 px-2 pb-2.5', collapsed && 'flex justify-center')}>
          <CommandPaletteTrigger collapsed={collapsed} />
        </div>

        <div
          className={cn(
            'min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-1 pt-1 pb-2 sm:px-2',
            '[&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb:hover]:bg-ring [&::-webkit-scrollbar-track]:bg-transparent',
            collapsed && 'px-2'
          )}
        >
          {!collapsed && (
            <div className="flex h-8 animate-in items-center px-2.5 text-xs font-medium text-sidebar-foreground/70 duration-300 ease-out-quad fade-in-0">
              Workspace
            </div>
          )}
          <SidebarMenu inlineCollapsed={collapsed} />
        </div>

        <div className={cn('shrink-0 pb-3', collapsed && 'pb-2')}>
          <div className={cn('px-1 pt-1 sm:px-2', collapsed && 'px-2')}>
            <SidebarMenu secondary inlineCollapsed={collapsed} />
          </div>
          <div className={cn('px-1 sm:px-2', collapsed && 'flex justify-center px-2')}>
            {showUserSkeleton ? (
              <SidebarUserSkeleton collapsed={collapsed} />
            ) : (
              <UserInfo data={data} collapsed={collapsed} />
            )}
          </div>
        </div>
      </div>
    </SidebarMenuProvider>
  );
}

export default GlobalSidebar;
